// Services
import { getEnvironmentApiUrl } from '../services/General';
import { groupScoreBySheetParameter } from '../services/Scores';

// Hooks
import { useContext, useCallback, useMemo } from 'react';
import useHttp from './useHttp';

// Context
import GeneralContext from '../context/GeneralContext';
import ScoresContext from '../context/ScoresContext';
import FiltersContext from '../context/FiltersContext';

// Interfaces
import { ConfigData } from '../interfaces/RequestConfig';
import { Score, ScoreGet } from '../interfaces/Score';
import { TerminalPhase } from '../interfaces/Phase';
import { Profile } from '../interfaces/Profile';

const useInitialRequest = (token: string | null) => {

    const { setGOOGLE_API_KEY, setHospitalLocation } = useContext(GeneralContext);
    const setScores = useContext(ScoresContext).setScores;
    const { setTerminalPhases, setProfiles } = useContext(FiltersContext);

    const { isLoading: loadingGeneralConfig, error: errorGeneralConfig, sendRequest: getGeneralConfig } = useHttp<ConfigData[]>();
    const { isLoading: loadingScores, error: errorScores, sendRequest: getScores } = useHttp<ScoreGet[]>();
    const { isLoading: loadingPhases, error: errorPhases, sendRequest: getPhases } = useHttp<TerminalPhase[]>();
    const { isLoading: loadingProfiles, error: errorProfiles, sendRequest: getEnvironments } = useHttp<Profile[]>();

    const headers = useMemo(() => {
        if (token === null) return;
        return { 'Content-Type': 'application/json', 'x-access-token': token };
    }, [token])

    const callGeneralConfig = useCallback(() => {
        getGeneralConfig({
            url: getEnvironmentApiUrl() + 'general_configuration/all',
            headers,
            method: 'POST',
            body: { params: {}, filter: {} },
        }, (data: ConfigData[]) => {
            const lat = Number(data[0].hospital_location.split('lat')[1].split(':')[1].split(',')[0]);
            const lng = Number(data[0].hospital_location.split('lng')[1].split(':')[1].split('}')[0]);
            if (data[0].google_api_key !== null) setGOOGLE_API_KEY(data[0].google_api_key);
            setHospitalLocation({ lat, lng });
        });
    }, [getGeneralConfig, headers, setGOOGLE_API_KEY, setHospitalLocation]);


    const callScores = useCallback(() => {
        getScores({
            url: getEnvironmentApiUrl() + 'score/all',
            headers,
            method: 'POST',
            body: { params: {}, filter: {} },
        }, (scores) => {
            const newScores: Score[] = scores.map(score => {
                return { ...score, score_parameters: groupScoreBySheetParameter(score) };
            });
            setScores(newScores);
        });
    }, [getScores, headers, setScores]);

    const callPhases = useCallback(() => {
        getPhases({
            url: getEnvironmentApiUrl() + 'terminal_phase/all',
            headers,
            method: 'POST',
            body: { params: {}, filter: {} }
        }, (data) => setTerminalPhases(data));
    }, [getPhases, headers, setTerminalPhases]);

    const callProfilesZones = useCallback(() => {
        getEnvironments({
            url: getEnvironmentApiUrl() + 'profile/all',
            headers,
            method: 'POST',
            body: {
                params: {},
                filter: { group_id: 0 }
            }
        }, (data) => setProfiles(data));
    }, [getEnvironments, headers, setProfiles]);

    const startRequests = useCallback(() => {
        callScores();
        callGeneralConfig();
        callPhases();
        callProfilesZones();
    }, [callGeneralConfig, callScores, callPhases, callProfilesZones]);

    const loading = loadingGeneralConfig || loadingPhases || loadingScores || loadingProfiles;
    const error = errorGeneralConfig || errorPhases || errorScores || errorProfiles;

    return { loading, error, startRequests };

};

export default useInitialRequest;